import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { AttachmentImage } from "@/components/ui/attachment-media";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/cn";
import { imagePixelSize } from "@/lib/image-frame-size";
import { CARD_PADDING } from "../shared/frame-content-padding";
import { useFrameSurface } from "../shared/use-frame-surface";
import { useT } from "@/i18n";
import { imageFrameMessages } from "@/i18n/messages/frames-image";

interface ImageItem {
  url: string;
  alt?: string;
  caption?: string;
  width?: number;
  height?: number;
}

/** A caption under its image, in the card's muted type. */
function Caption({ text, className }: { text: string; className?: string }) {
  return (
    <figcaption
      className={cn("mt-[0.6em] text-[0.84em] text-card-muted", className)}
    >
      {text}
    </figcaption>
  );
}

/**
 * An image frame opened. One image fills the window edge to edge, as its
 * card does (the window takes the image's aspect). Several images are a
 * column at the card's padding, each at its own aspect, captions below.
 */
export function ImageFrame({ id, data, selected }: FrameProps) {
  const t = useT(imageFrameMessages);
  const frameSurface = useFrameSurface(id);
  const d = data;
  const images = (d.content?.images as ImageItem[] | undefined) ?? [];
  const single = images.length === 1 ? images[0]! : null;

  return (
    <div
      className={cn(
        frameSurface.className,
        "relative flex h-full flex-col overflow-hidden",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="image-frame"
      {...frameSurface.attributes}
    >
      <div
        className={cn(
          "scrollbar-none nodrag min-h-0 flex-1",
          single ? "flex flex-col overflow-hidden" : "overflow-auto",
          selected && "nowheel",
        )}
        data-frame-content="true"
      >
        {d.status === "streaming" && images.length === 0 ? (
          <div className={cn("flex items-center gap-2", CARD_PADDING)}>
            <Spinner className="h-3 w-3" />
            <span className="text-canvas-base text-muted">
              {t("loadingImages")}
            </span>
          </div>
        ) : single ? (
          <figure
            className="flex min-h-0 flex-1 flex-col"
            data-testid="image-item-0"
          >
            <AttachmentImage
              src={single.url}
              alt={single.alt ?? t("imageAlt", { number: 1 })}
              className="block h-full min-h-0 w-full flex-1 object-cover"
              loading="lazy"
            />
            {single.caption && (
              <Caption
                text={single.caption}
                className="px-[1.6em] pb-[1.1em]"
              />
            )}
          </figure>
        ) : images.length > 0 ? (
          <div className={cn("flex flex-col gap-[1.2em]", CARD_PADDING)}>
            {images.map((img, idx) => {
              const size = imagePixelSize(img);
              return (
                <figure key={img.url || idx} data-testid={`image-item-${idx}`}>
                  <AttachmentImage
                    src={img.url}
                    alt={img.alt ?? t("imageAlt", { number: idx + 1 })}
                    className="block h-auto w-full"
                    style={
                      size
                        ? { aspectRatio: `${size.width} / ${size.height}` }
                        : undefined
                    }
                    loading="lazy"
                  />
                  {img.caption && <Caption text={img.caption} />}
                </figure>
              );
            })}
          </div>
        ) : (
          <p
            className={cn(
              "text-center text-canvas-base italic text-muted",
              CARD_PADDING,
            )}
          >
            {t("noImagesYet")}
          </p>
        )}
      </div>
    </div>
  );
}
